import React, { useState, useEffect } from 'react';
import { Form, InputNumber, Button, Popconfirm, Tooltip, AutoComplete, Spin, Switch, Dropdown } from 'antd';
import { SaveOutlined, DeleteOutlined, SettingOutlined, EditOutlined, ThunderboltOutlined, SearchOutlined, StopOutlined, InfoCircleOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { FutureInstrument } from '../../../types';
import apiService, { ServiceType } from '../../../services/api';
import {
  Card,
  FormCard,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormItem,
  SubmitButton,
} from '../styles';
import { formatNumber, formatDecimal } from '../../../utils/numberFormat';
import CorporateActionBadge from '../../../components/CorporateActionBadge';
import CorporateActionPanel, { CorporateActionStatusTag } from '../../../components/CorporateActionPanel';
import ExRightsPanel from '../../../components/ExRightsPanel';
import type { BotPhase } from '../../../hooks/useExecutionStatus';

interface VolumePriceRange {
  key: string;
  minPrice: number;
  maxPrice: number;
  volume: number;
}

export interface PriceLevelEntry {
  price: number;
  quantity: number;
}

interface GridTradingFormValues {
  allow_to_exceed_floor_ceiling: boolean;
  grid_interval: number;
  volume_by_price_range: number[][];
  grid_type: 'BULL' | 'BEAR';
  grid_origin: number;
  grid_height: number;
  capacity: number;
  stop_loss_quantity: number;
  stop_operating_threshold: number;
  forced_close: boolean;
}

/** The groups the compact shell shows one at a time. */
export type MobileConfigSection = 'settings' | 'levels' | 'advanced';

interface ConfigurationSectionProps {
  form: any;
  volumeRanges: VolumePriceRange[];
  loading: boolean;
  selectedInstrument: FutureInstrument | null;
  displayPrice: number;
  displayChangePercent: number;
  onFinish: (values: GridTradingFormValues) => void;
  onAddRange: () => void;
  onDeleteRange: (key: string) => void;
  onRangeChange: (key: string, field: keyof VolumePriceRange, value: number) => void;
  onRangeUpdate?: (key: string, payload: Partial<VolumePriceRange>) => void;
  isExistingConfig: boolean;
  configsLoading: boolean;
  onPlaceGrid?: () => void;
  placingGrid?: boolean;
  onSetRanges?: (ranges: VolumePriceRange[]) => void;
  rangesManuallyEdited?: boolean;
  onDeleteConfig?: () => void;
  deletingConfig?: boolean;
  /** When true, the grid bot is running — symbol removal is blocked (BE rejects with 409). */
  isBotRunning?: boolean;
  /** Server-side phase of the grid bot — drives which of Stop / Place is offered. */
  botPhase?: BotPhase;
  onStopGrid?: () => void;
  stoppingGrid?: boolean;
  onStopAll?: () => void;
  stoppingAll?: boolean;
  /** The edited symbol is paused on the running bot: offer Start instead of Stop. */
  symbolPaused?: boolean;
  onResumeGrid?: () => void;
  resumingGrid?: boolean;
  priceLevels?: PriceLevelEntry[];
  onPriceLevelsChange?: (levels: PriceLevelEntry[]) => void;
  positionMatchedQty?: Record<number, { long: number; short: number }>;
  onFormDirty?: () => void;
  formDirty?: boolean;
  searchResults?: { symbol: string; price: number }[];
  searchLoading?: boolean;
  onSearch?: (value: string) => void;
  onSelectStock?: (symbol: string) => void;
  editingSymbol?: string | null;
  portfolioId?: string | null;
  dataConfigId?: string | null;
  onClosePosition?: () => void;
  onDisableClosePosition?: () => void;
  closingPosition?: boolean;
  savedForcedClose?: boolean;
  /** Content rendered as a sticky header inside the Trading Parameters card (e.g. capital panel). */
  topSlot?: React.ReactNode;
  /** Compact shell: drop the card chrome and show one section at a time. */
  mobile?: boolean;
  mobileSection?: MobileConfigSection;
  /** Validation failed on a field the active section may not be showing. */
  onFinishFailed?: (err: unknown) => void;
}

const ConfigurationSection: React.FC<ConfigurationSectionProps> = ({
  form,
  volumeRanges: _volumeRanges,
  loading,
  selectedInstrument,
  displayPrice,
  displayChangePercent,
  onFinish,
  onAddRange: _onAddRange,
  onDeleteRange: _onDeleteRange,
  onRangeChange: _onRangeChange,
  onRangeUpdate: _onRangeUpdate,
  isExistingConfig,
  configsLoading,
  onPlaceGrid,
  placingGrid = false,
  onSetRanges,
  rangesManuallyEdited = false,
  onDeleteConfig,
  deletingConfig = false,
  isBotRunning = false,
  botPhase = 'unknown',
  onStopGrid,
  stoppingGrid = false,
  onStopAll,
  stoppingAll = false,
  symbolPaused = false,
  onResumeGrid,
  resumingGrid = false,
  priceLevels = [],
  onPriceLevelsChange,
  positionMatchedQty = {},
  onFormDirty,
  formDirty = false,
  searchResults = [],
  searchLoading = false,
  onSearch,
  onSelectStock,
  editingSymbol,
  dataConfigId,
  portfolioId,
  onClosePosition,
  onDisableClosePosition,
  closingPosition = false,
  savedForcedClose = false,
  topSlot,
  mobile = false,
  mobileSection = 'settings',
  onFinishFailed,
}) => {
  // Format integer part with commas, keep decimal part untouched so typing "18." doesn't
  // collapse to "18" on the next keystroke.
  const numFormatter = (value: number | string | undefined) => {
    if (value === undefined || value === null || value === '') return '';
    const s = String(value);
    const [intPart, decPart] = s.split('.');
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
  };
  // Strip commas; return the raw string so antd preserves a trailing "." while the user is
  // mid-typing a decimal (Number("18.") === 18 would otherwise eat the dot and reset cursor).
  const numParser = ((value: string | undefined) => (value ? value.replace(/,/g, '') : '')) as never;
  const [searchValue, setSearchValue] = useState(selectedInstrument?.symbol || '');
  const [maxCapacity, setMaxCapacity] = useState<number | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [calculatedCapital, setCalculatedCapital] = useState<number>(0);

  useEffect(() => {
    setSearchValue(selectedInstrument?.symbol || '');
  }, [selectedInstrument?.symbol]);

  // Fetch max capacity from API when symbol changes
  useEffect(() => {
    if (!editingSymbol || !portfolioId) {
      setMaxCapacity(null);
      return;
    }
    let cancelled = false;
    const coreService = apiService.getService(ServiceType.CORE);
    coreService.get('/accounting/max-capacity', { params: { symbol: editingSymbol, portfolio_id: portfolioId } })
      .then(res => {
        if (!cancelled) setMaxCapacity(res.data?.maxCapacity ?? null);
      })
      .catch((err) => {
        console.error('[MaxCapacity] error:', editingSymbol, err);
        if (!cancelled) setMaxCapacity(null);
      });
    return () => { cancelled = true; };
  }, [editingSymbol, portfolioId]);

  // Sync calculatedCapital from form value when loading existing config
  useEffect(() => {
    const existing = form.getFieldValue('capacity');
    if (existing > 0) setCalculatedCapital(existing);
  }, [selectedInstrument]); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate price levels on initial load when form has values but priceLevels is empty
  useEffect(() => {
    if (priceLevels.length > 0) return;
    const origin = form.getFieldValue('grid_origin');
    const height = form.getFieldValue('grid_height');
    const interval = form.getFieldValue('grid_interval');
    if (origin && height && interval) {
      const newLevels = generatePriceLevels(origin, height, interval);
      if (newLevels.length > 0) {
        onPriceLevelsChange?.(newLevels);
      }
    }
  }, [selectedInstrument]); // eslint-disable-line react-hooks/exhaustive-deps

  const autoFillRanges = (origin: number, height: number, interval: number) => {
    const gridLimit = Number(origin) - Number(height) * Number(interval);
    onSetRanges?.([{ key: `range-${Date.now()}`, minPrice: gridLimit, maxPrice: Number(origin), volume: 100 }]);
  };

  // Generate price levels from grid params
  const generatePriceLevels = (origin: number, height: number, interval: number) => {
    const prices: number[] = [];
    for (let i = 0; i <= height; i++) {
      const price = Math.round((origin - i * interval) * 100) / 100;
      if (price <= 0) break;
      prices.push(price);
    }
    const currentQty = priceLevels.length > 0 ? priceLevels[0].quantity : 100;
    const levels: PriceLevelEntry[] = prices.map(price => {
      const existing = priceLevels.find(l => Math.abs(l.price - price) < 1e-9);
      return { price, quantity: existing?.quantity ?? currentQty };
    });
    return levels;
  };

  const autoCalculateCapital = (levels: PriceLevelEntry[]) => {
    const total = Math.round(levels.reduce((sum, l) => sum + l.price * 1000 * l.quantity, 0));
    form.setFieldValue('capacity', total);
    setCalculatedCapital(total);
    return total;
  };

  const handleSetAllQuantity = (qty: number) => {
    const updatedLevels = priceLevels.map(l => ({ ...l, quantity: qty }));
    onPriceLevelsChange?.(updatedLevels);
    autoCalculateCapital(updatedLevels);
    onFormDirty?.();
  };

  const hasMatchedPositions = Object.values(positionMatchedQty).some(
    (m) => (m.long ?? 0) + (m.short ?? 0) > 0
  );

  const outsidePositions = (() => {
    const priceLevelSet = new Set(priceLevels.map(l => l.price));
    return Object.entries(positionMatchedQty)
      .filter(([priceStr]) => !priceLevelSet.has(Number(priceStr)))
      .map(([priceStr, matched]) => ({
        price: Number(priceStr),
        long: matched.long ?? 0,
        short: matched.short ?? 0,
        total: (matched.long ?? 0) + (matched.short ?? 0),
      }))
      .filter(p => p.total > 0)
      .sort((a, b) => b.price - a.price);
  })();

  const handleValuesChange = (changedValues: any) => {
    onFormDirty?.();
    const gridChanged = 'grid_origin' in changedValues || 'grid_height' in changedValues || 'grid_interval' in changedValues;
    if (!gridChanged) return;
    const origin = form.getFieldValue('grid_origin');
    const height = form.getFieldValue('grid_height');
    const interval = form.getFieldValue('grid_interval');
    if (origin && height && interval) {
      if (!rangesManuallyEdited) {
        autoFillRanges(origin, height, interval);
      }
      const newLevels = generatePriceLevels(origin, height, interval);
      onPriceLevelsChange?.(newLevels);
      autoCalculateCapital(newLevels);
    }
  };

  // Same control in both shells: the Card header on desktop, the Advanced section on
  // mobile — there is no header row there to hang it off.
  const deleteControl = (
          isExistingConfig && onDeleteConfig ? (
            isBotRunning ? (
              <Tooltip title="Cannot remove a symbol while the bot is running. Stop the bot first.">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<InfoCircleOutlined />}
                  disabled
                  style={{ fontSize: 12 }}
                >
                  Delete
                </Button>
              </Tooltip>
            ) : hasMatchedPositions ? (
              <Tooltip title="Cannot delete configuration while positions are matched">
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<InfoCircleOutlined />}
                  disabled
                  style={{ fontSize: 12 }}
                >
                  Delete
                </Button>
              </Tooltip>
            ) : (
              <Popconfirm
                title="Delete this configuration?"
                description="This will remove all saved parameters for this symbol."
                onConfirm={onDeleteConfig}
                okText="Delete"
                cancelText="Cancel"
                okButtonProps={{ danger: true, size: 'middle', loading: deletingConfig, style: { minWidth: 72, height: 32 } }}
                cancelButtonProps={{ size: 'middle', style: { minWidth: 72, height: 32 } }}
              >
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  loading={deletingConfig}
                  style={{ fontSize: 12 }}
                >
                  Delete
                </Button>
              </Popconfirm>
            )
          ) : null
  );

  /**
   * Compact shell renders one group at a time. The inactive groups stay mounted but
   * hidden, so a submit from any section still validates every field — unmounting them
   * would let an invalid Upper Bound through from the Levels view.
   */
  const sectionStyle = (name: MobileConfigSection): React.CSSProperties | undefined =>
    mobile && mobileSection !== name ? { display: 'none' } : undefined;

  return (
    <Card
      data-tour="grid-params"
      title={mobile ? undefined : 'Trading Parameters'}
      extra={mobile ? undefined : deleteControl}
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // Compact shell: the card grows to its content and the page scrolls it. Clamping it
        // to the viewport here is what made the form scroll inside a few hundred pixels
        // while the chart above kept its own space.
        ...(mobile
          ? { height: 'auto', overflow: 'visible', background: 'transparent', border: 'none' }
          : { height: '100%', overflow: 'hidden' }),
      }}
      styles={{ body: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        padding: mobile ? '0' : '12px',
        ...(mobile
          ? { flex: 'none', overflow: 'visible', height: 'auto' }
          : { flex: 1, overflow: 'hidden', height: 0 }),
      } }}
    >
      {topSlot ? (
        <div style={{ flexShrink: 0, marginBottom: 8 }}>
          {topSlot}
        </div>
      ) : null}
      <div style={{
        overflowX: 'hidden',
        minHeight: 0,
        WebkitOverflowScrolling: 'touch',
        // One scroller per screen: on mobile this content is part of the page column the
        // shell scrolls, so it must not open a second scroll region of its own.
        ...(mobile
          ? { flex: 'none', overflowY: 'visible' }
          : { flex: 1, overflowY: 'scroll', paddingRight: '4px', scrollbarGutter: 'stable' }),
      }}>
        <FormCard style={{ marginBottom: '8px' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            onValuesChange={handleValuesChange}
            scrollToFirstError={{ behavior: 'smooth', block: 'center' }}
            initialValues={{
              grid_type: 'BULL',
              allow_to_exceed_floor_ceiling: true,
              stop_loss_quantity: 0,
              stop_operating_threshold: 1000,
              forced_close: false
            }}
          >
            <div style={sectionStyle('settings')}>
            {/* Main settings — always visible */}
            <FormSection>
              <SectionTitle>
                <SettingOutlined style={{ color: '#0ECB81', marginRight: '8px' }} />
                Grid Settings
                {/* Says so when these numbers were rewritten by an ex-rights adjustment
                    rather than typed. */}
                {editingSymbol && (
                  <span style={{ marginLeft: 12 }}>
                    <CorporateActionBadge symbols={[editingSymbol]} />
                  </span>
                )}
              </SectionTitle>
              <SectionDescription>Configure your grid strategy</SectionDescription>

              {/* Hidden capacity field — the value comes from `Deposited to {symbol}` in
                  the capital panel above. The form still needs the field for submission. */}
              <FormItem name="capacity" hidden>
                <InputNumber />
              </FormItem>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <FormItem
                  label="Search Stock"
                  style={{ flex: 1, marginBottom: 0 }}
                >
                  <AutoComplete
                    size="small"
                    style={{ width: '100%' }}
                    value={searchValue}
                    onChange={setSearchValue}
                    options={searchResults.map((f) => ({
                      value: f.symbol,
                      label: `${f.symbol} — ${formatDecimal(f.price, 2)}`
                    }))}
                    onSearch={onSearch}
                    onSelect={(val) => { setSearchValue(val); onSelectStock?.(val); }}
                    placeholder="Search stock..."
                    notFoundContent={searchLoading ? <Spin size="small" /> : null}
                    suffixIcon={<SearchOutlined style={{ fontSize: 12, color: '#848E9C' }} />}
                    allowClear
                  />
                </FormItem>
              </div>

              <FormGrid>
                <FormItem
                  name="grid_origin"
                  label="Upper Bound"
                  rules={[{ required: true, message: 'Required' }, { type: 'number', min: 0.1, message: 'Must be > 0' }]}
                >
                  {/* No custom formatter/parser: antd's default decimal handling preserves
                      the trailing '.' while typing "20." → "20.5". A custom parser that
                      called Number() ate the dot and reset the cursor. */}
                  {/* One decimal, because that is the tick: 0.1 thousand VND = 100 VND. Two
                      decimals let an operator type 20.05, which is not a price the exchange
                      accepts — every level derived from it falls off tick and the orders are
                      rejected or silently skipped. */}
                  <InputNumber
                    size="small"
                    placeholder={displayPrice > 0 ? String(displayPrice) : 'Enter upper bound'}
                    style={{ width: '100%' }}
                    precision={1}
                    step={0.1}
                    min={0.1}
                  />
                </FormItem>

                <FormItem
                  name="grid_interval"
                  label="Grid Interval"
                  rules={[{ required: true, message: 'Required' }]}
                >
                  <InputNumber size="small" placeholder="0.3" style={{ width: '100%' }} min={0.1} step={0.1} precision={1} />
                </FormItem>

                {/* The grid's lowest level is `origin − height × interval`. Let that go
                    negative and `_open_all_grid_positions` takes its ABSOLUTE value, inventing
                    levels that can sit ABOVE the origin — and a buy at an invented level is
                    clamped to the CEILING price rather than skipped. Nothing downstream
                    catches it, so the form has to. */}
                <FormItem
                  name="grid_height"
                  label="No. of Intervals"
                  dependencies={['grid_origin', 'grid_interval']}
                  rules={[
                    { required: true, message: 'Required' },
                    { type: 'number', min: 1, message: 'Min 1' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const origin = Number(getFieldValue('grid_origin'));
                        const interval = Number(getFieldValue('grid_interval'));
                        const height = Number(value);
                        if (!origin || !interval || !height) return Promise.resolve();
                        const lowest = origin - height * interval;
                        if (lowest > 0) return Promise.resolve();
                        const max = Math.floor((origin - interval) / interval);
                        return Promise.reject(
                          new Error(
                            // A raw grid parameter in a validation message, not a displayed price — numberFormat-exempt
                            `Lowest level would be ${lowest.toFixed(1)} — must stay above 0. Max ${max} at this interval.`,
                          ),
                        );
                      },
                    }),
                  ]}
                >
                  <InputNumber size="small" placeholder="10" style={{ width: '100%' }} min={1} precision={0} formatter={numFormatter} parser={numParser} />
                </FormItem>

                <FormItem label="Quantity (per level)" style={{ marginBottom: 0 }}>
                  <InputNumber
                    size="small"
                    placeholder="100"
                    style={{ width: '100%' }}
                    min={100}
                    step={100}
                    value={priceLevels.length > 0 ? priceLevels[0].quantity : undefined}
                    onChange={(val) => { if (val != null) handleSetAllQuantity(Math.round(val / 100) * 100 || 100); }}
                    formatter={numFormatter}
                    parser={numParser}
                  />
                </FormItem>

              </FormGrid>
            </FormSection>
            </div>

            <div style={sectionStyle('levels')}>
            {/* Price Levels — generated from grid params */}
            <FormSection>
              <SectionTitle>
                <SettingOutlined style={{ color: '#0ECB81', marginRight: '8px' }} />
                Price Levels
                <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {priceLevels.length > 0 && (
                    <span style={{ fontSize: '12px', fontWeight: 400, color: '#848E9C' }}>
                      Qty/level: <span style={{ color: '#E0E0E0', fontWeight: 600 }}>{formatNumber(priceLevels[0]?.quantity)}</span>
                    </span>
                  )}
                  {/* Next to the numbers it rewrites: an ex-rights adjustment re-bases exactly
                      these prices and quantities. A status chip, not a button — adjusting is
                      platform work, see CorporateActionPanel. */}
                  <CorporateActionStatusTag
                    dataConfigId={dataConfigId}
                    symbol={editingSymbol}
                  />
                </span>
              </SectionTitle>
              <SectionDescription>Price levels generated from grid parameters</SectionDescription>
                  <div style={{ marginBottom: '4px' }}>
                    {priceLevels.length === 0 ? (
                      <div style={{ color: '#848E9C', fontSize: '12px', padding: '12px 0' }}>Set Upper Bound, Grid Interval, and Number of Intervals to generate price levels</div>
                    ) : (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 70px', gap: '0', fontSize: '12px' }}>
                        <div style={{ padding: '2px 8px', color: '#848E9C', fontWeight: 600, borderBottom: '1px solid #1E2329' }}>Price</div>
                        <div style={{ padding: '2px 8px', color: '#848E9C', fontWeight: 600, borderBottom: '1px solid #1E2329' }}>Quantity</div>
                        <div style={{ padding: '2px 8px', color: '#848E9C', fontWeight: 600, borderBottom: '1px solid #1E2329', textAlign: 'center' }}>Matched</div>
                        {priceLevels.map((level, idx) => {
                          const matched = positionMatchedQty[level.price];
                          const matchedLong = matched?.long ?? 0;
                          const matchedShort = matched?.short ?? 0;
                          const matchedTotal = matchedLong + matchedShort;
                          return (
                          <React.Fragment key={level.price}>
                            <div style={{
                              padding: '4px 8px',
                              borderBottom: '1px solid #1E2329',
                              color: idx === 0 ? '#0ECB81' : idx === priceLevels.length - 1 ? '#F6465D' : '#E0E0E0',
                              fontWeight: idx === 0 || idx === priceLevels.length - 1 ? 600 : 400,
                            }}>
                              {formatDecimal(level.price, 1)}
                              {idx === 0 && <span style={{ marginLeft: '4px', opacity: 0.7 }}>U</span>}
                              {idx === priceLevels.length - 1 && <span style={{ marginLeft: '4px', opacity: 0.7 }}>L</span>}
                            </div>
                            <div style={{ padding: '4px 8px', borderBottom: '1px solid #1E2329', color: '#E0E0E0' }}>
                              {formatNumber(level.quantity)}
                            </div>
                            <div style={{
                              padding: '4px 8px',
                              borderBottom: '1px solid #1E2329',
                              textAlign: 'center',
                              color: matchedTotal > 0 ? '#0ECB81' : '#3d4148',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}>
                              {matchedTotal > 0 ? (
                                <Tooltip title={`Long: ${matchedLong} | Short: ${matchedShort}`}>
                                  <span>{matchedTotal}</span>
                                </Tooltip>
                              ) : '—'}
                            </div>
                          </React.Fragment>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Positions outside current price levels */}
                  {outsidePositions.length > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '11px', color: '#F0B90B', fontWeight: 600, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <InfoCircleOutlined style={{ fontSize: 11 }} />
                        Positions outside grid
                      </div>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 70px',
                        gap: '0',
                        fontSize: '12px',
                        background: 'rgba(240, 185, 11, 0.05)',
                        border: '1px solid rgba(240, 185, 11, 0.15)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                      }}>
                        <div style={{ padding: '2px 8px', color: '#848E9C', fontWeight: 600, borderBottom: '1px solid rgba(240, 185, 11, 0.15)' }}>Price</div>
                        <div style={{ padding: '2px 8px', color: '#848E9C', fontWeight: 600, borderBottom: '1px solid rgba(240, 185, 11, 0.15)' }}>Long</div>
                        <div style={{ padding: '2px 8px', color: '#848E9C', fontWeight: 600, borderBottom: '1px solid rgba(240, 185, 11, 0.15)', textAlign: 'center' }}>Short</div>
                        {outsidePositions.map(pos => (
                          <React.Fragment key={pos.price}>
                            <div style={{ padding: '4px 8px', borderBottom: '1px solid rgba(240, 185, 11, 0.1)', color: '#F0B90B', fontWeight: 500 }}>
                              {formatDecimal(pos.price, 1)}
                            </div>
                            <div style={{ padding: '4px 8px', borderBottom: '1px solid rgba(240, 185, 11, 0.1)', color: pos.long > 0 ? '#0ECB81' : '#3d4148' }}>
                              {pos.long > 0 ? formatNumber(pos.long) : '—'}
                            </div>
                            <div style={{ padding: '4px 8px', borderBottom: '1px solid rgba(240, 185, 11, 0.1)', textAlign: 'center', color: pos.short > 0 ? '#F6465D' : '#3d4148' }}>
                              {pos.short > 0 ? formatNumber(pos.short) : '—'}
                            </div>
                          </React.Fragment>
                        ))}
                      </div>
                      <FormItem name="stop_loss_quantity" hidden>
                        <InputNumber />
                      </FormItem>
                    </div>
                  )}
            </FormSection>
            </div>

            {/* Before the panel below, because this one asks for a decision and that one
                only explains. Deliberately NOT wrapped in a sectionStyle: on mobile that
                would hide it unless the user happened to open the right sub-section, and a
                deadline nobody scrolled to is the exact failure this panel exists to
                prevent. */}
            <ExRightsPanel dataConfigId={dataConfigId} />

            {/* Why the bot will not start and what has already been applied to this config.
                Read-only: an ex-rights date is not the owner's decision to make. */}
            <div style={sectionStyle('advanced')}>
              <CorporateActionPanel dataConfigId={dataConfigId} />
            </div>

            <div style={sectionStyle('settings')}>
            {/* Symbol status + action buttons */}
            {editingSymbol && (
              <div data-tour="grid-save" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '8px',
                padding: '8px 10px',
                marginTop: '6px',
                borderRadius: '6px',
                background: isExistingConfig
                  ? 'linear-gradient(135deg, rgba(14, 203, 129, 0.08) 0%, rgba(14, 203, 129, 0.03) 100%)'
                  : 'linear-gradient(135deg, rgba(240, 185, 11, 0.08) 0%, rgba(240, 185, 11, 0.03) 100%)',
                border: isExistingConfig ? '1px solid rgba(14, 203, 129, 0.2)' : '1px solid rgba(240, 185, 11, 0.2)',
              }}>
                <div style={{ fontSize: '11px', color: '#B7BDC6', lineHeight: 1.4 }}>
                  {isExistingConfig ? (
                    <span>
                      <span style={{ color: '#0ECB81', fontWeight: 700 }}>{editingSymbol}</span>
                      {' '}is configured. Edit and click Update to change.
                    </span>
                  ) : (
                    <span>
                      <span style={{ color: '#F0B90B', fontWeight: 700 }}>{editingSymbol}</span>
                      {' '}is not saved yet. Configure and save to apply these grid settings.
                    </span>
                  )}
                </div>
                {!mobile && (
                <Tooltip title={savedForcedClose ? 'Close position is active. Disable it before updating.' : undefined}>
                  <SubmitButton
                    type="primary"
                    size="small"
                    icon={isExistingConfig ? <EditOutlined /> : <SaveOutlined />}
                    onClick={() => form.submit()}
                    loading={loading}
                    disabled={(isExistingConfig && !formDirty) || savedForcedClose}
                    style={{ minWidth: '80px', height: '26px', fontSize: '12px', flexShrink: 0 }}
                  >
                    {isExistingConfig ? 'Update' : 'Save'}
                  </SubmitButton>
                </Tooltip>
                )}
              </div>
            )}
            </div>

            {/* Advanced Settings */}
            <div style={sectionStyle('advanced')}>
            {isExistingConfig && (
              <div style={{ marginTop: mobile ? 0 : '8px' }}>
                {/* The compact shell already gives Advanced its own pill, so a collapse
                    header inside it is a second lid on the same box — and it hid the only
                    control in the section behind an extra tap. */}
                <div
                  onClick={() => setShowAdvanced(prev => !prev)}
                  style={{ display: mobile ? 'none' : 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', fontSize: '11px', color: '#848E9C', userSelect: 'none', padding: '4px 0' }}
                >
                  <DownOutlined style={{ fontSize: 9, transition: 'transform 0.2s', transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)' }} />
                  Advanced
                  {savedForcedClose && (
                    <span style={{ marginLeft: '4px', color: '#F6465D', fontWeight: 600 }}>• Close pending</span>
                  )}
                </div>
                {(mobile || showAdvanced) && (
                  <div style={{
                    padding: '10px 12px',
                    marginTop: '4px',
                    borderRadius: '6px',
                    background: savedForcedClose ? 'rgba(246, 70, 93, 0.06)' : '#1E2329',
                    border: `1px solid ${savedForcedClose ? 'rgba(246, 70, 93, 0.25)' : '#2B3139'}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: 600, color: '#E0E0E0', marginBottom: '2px' }}>
                          Force Close Position
                        </div>
                        {/* The engine re-reads `forced_close` every tick (see the grid
                            worker's main loop), so a running bot acts on it at once and a
                            stopped one waits for its next run. Saying only "next grid run"
                            understated it for the case that matters most. */}
                        <div style={{ fontSize: '11px', color: '#848E9C', lineHeight: 1.4 }}>
                          {savedForcedClose
                            ? isBotRunning
                              ? 'Enabled — the running bot is closing positions now'
                              : 'Enabled — positions will be closed when the grid next starts'
                            : isBotRunning
                              ? 'Sell all held positions, starting on the next tick'
                              : 'Sell all held positions when the grid next starts'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                        <Popconfirm
                          title={savedForcedClose ? 'Disable Close Position' : 'Close Position'}
                          description={savedForcedClose
                            ? isBotRunning
                              ? `This will cancel forced close for ${editingSymbol}. The bot is running, so it resumes opening positions on the next tick. Are you sure?`
                              : `This will cancel forced close for ${editingSymbol}. Are you sure?`
                            : isBotRunning
                              ? `This will close all held positions for ${editingSymbol}. The bot is running, so it applies on the next tick. Are you sure?`
                              : `This will close all held positions for ${editingSymbol} when the grid next starts. Are you sure?`}
                          onConfirm={savedForcedClose ? onDisableClosePosition : onClosePosition}
                          okText={savedForcedClose ? 'Disable' : 'Close Position'}
                          cancelText="Cancel"
                          okButtonProps={savedForcedClose
                            ? { size: 'middle', style: { minWidth: 72, height: 32, background: '#0ECB81', borderColor: '#0ECB81' } }
                            : { danger: true, size: 'middle', loading: closingPosition, style: { minWidth: 72, height: 32 } }}
                          cancelButtonProps={{ size: 'middle', style: { minWidth: 72, height: 32, background: '#2B3139', borderColor: '#3d4148', color: '#E0E0E0' } }}
                        >
                          <Switch
                            checked={savedForcedClose}
                            loading={closingPosition}
                            checkedChildren="On"
                            unCheckedChildren="Off"
                          />
                        </Popconfirm>
                      </div>
                    </div>
                  </div>
                )}
                {/* Removing the symbol belongs with the other destructive control, not
                    floating on a row of its own above the section. */}
                {mobile && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                    {deleteControl}
                  </div>
                )}
              </div>
            )}
            </div>
          </Form>
        </FormCard>
      </div>
      {!mobile && (
        <ConfigActionBar
          form={form}
          isExistingConfig={isExistingConfig}
          editingSymbol={editingSymbol}
          loading={loading}
          formDirty={formDirty}
          savedForcedClose={savedForcedClose}
          onStopGrid={onStopGrid}
          stoppingGrid={stoppingGrid}
          onStopAll={onStopAll}
          stoppingAll={stoppingAll}
          symbolPaused={symbolPaused}
          onResumeGrid={onResumeGrid}
          resumingGrid={resumingGrid}
          onPlaceGrid={onPlaceGrid}
          placingGrid={placingGrid}
          botPhase={botPhase}
        />
      )}
    </Card>
  );
};

/** Props the pinned action row needs — the same set in both shells. */
export interface ConfigActionBarProps {
  form: any;
  mobile?: boolean;
  isExistingConfig: boolean;
  editingSymbol?: string | null;
  loading: boolean;
  formDirty: boolean;
  savedForcedClose: boolean;
  /** Pause the edited symbol only; the other symbols keep trading. */
  onStopGrid?: () => void;
  stoppingGrid?: boolean;
  /** Stop the whole bot — every symbol. */
  onStopAll?: () => void;
  stoppingAll?: boolean;
  /** The edited symbol is paused on the running bot: offer Start instead of Stop. */
  symbolPaused?: boolean;
  onResumeGrid?: () => void;
  resumingGrid?: boolean;
  onPlaceGrid?: () => void;
  placingGrid?: boolean;
  /** `unknown` (not loaded / unreadable) offers both and leaves the server to refuse. */
  botPhase?: BotPhase;
}

/**
 * Save/Update, Stop (drop-up: stop or start this symbol / stop all) and Place Grid.
 *
 * Its own component because the compact shell renders it OUTSIDE the scrolling column.
 * `position: sticky; bottom: 0` cannot pin a scroller's last child — there is nothing below
 * it to stick against — so the only way to keep these reachable without scrolling a long
 * config to its end is to hoist them out of the scroll container entirely.
 */
export const ConfigActionBar: React.FC<ConfigActionBarProps> = ({
  form,
  mobile = false,
  isExistingConfig,
  editingSymbol,
  loading,
  formDirty,
  savedForcedClose,
  onStopGrid,
  stoppingGrid = false,
  onStopAll,
  stoppingAll = false,
  symbolPaused = false,
  onResumeGrid,
  resumingGrid = false,
  onPlaceGrid,
  placingGrid = false,
  botPhase = 'unknown',
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<'symbol' | 'all' | null>(null);
  if (!isExistingConfig && !mobile) return null;
  // Stop only while the bot is up; Place only while it is down. Stop All stays pressable
  // while stopping so a stop that is not landing can be re-sent.
  const stopping = botPhase === 'stopping';
  const starting = botPhase === 'starting';
  const canStop = botPhase === 'unknown' || botPhase === 'running' || starting;
  const canStopAll = canStop || stopping;
  const canPlace = botPhase === 'unknown' || botPhase === 'stopped';
  const stopHint = stopping
    ? 'The bot is stopping — it finishes cancelling orders and closing its books first.'
    : botPhase === 'stopped'
      ? 'The grid bot is not running.'
      : undefined;
  const stopConfirm = confirmTarget === 'symbol' && symbolPaused
    ? {
        title: `Start ${editingSymbol}`,
        description: `Start trading ${editingSymbol} again with its current grid and capital?`,
        okText: 'Start',
        onConfirm: onResumeGrid,
      }
    : confirmTarget === 'symbol'
    ? {
        title: `Stop ${editingSymbol}`,
        description: `Stop trading ${editingSymbol} and cancel its unfilled orders? The other symbols keep running.`,
        okText: 'Stop',
        onConfirm: onStopGrid,
      }
    : {
        title: 'Stop All',
        description: stopping
          ? 'The bot is already stopping. Send the stop again?'
          : 'Stop the whole grid bot? Every symbol stops and all unfilled orders are cancelled.',
        okText: 'Stop All',
        onConfirm: onStopAll,
      };
  return (
  <div data-tour="grid-run" style={{ flexShrink: 0, padding: mobile ? 0 : '8px 0 4px', display: 'flex', gap: '6px' }}>
    {/* Compact shell: Save/Update sits in the pinned bar instead of mid-form, so
        committing a change never means scrolling a long config back to find it. */}
    {mobile && (
      <Tooltip title={savedForcedClose ? 'Close position is active. Disable it before updating.' : undefined}>
        <SubmitButton
          type="primary"
          icon={isExistingConfig ? <EditOutlined /> : <SaveOutlined />}
          onClick={() => form.submit()}
          loading={loading}
          disabled={(isExistingConfig && !formDirty) || savedForcedClose}
          style={{ flex: 1, height: '40px', fontSize: '13px', fontWeight: 600 }}
        >
          {isExistingConfig ? 'Update' : 'Save'}
        </SubmitButton>
      </Tooltip>
    )}
    {isExistingConfig && (
    <>
    {/* One Stop: the drop-up picks the scope, the Popconfirm asks before acting. */}
    <Popconfirm
      open={confirmTarget !== null}
      onOpenChange={(open) => { if (!open) setConfirmTarget(null); }}
      title={stopConfirm.title}
      description={stopConfirm.description}
      onConfirm={() => { setConfirmTarget(null); stopConfirm.onConfirm?.(); }}
      onCancel={() => setConfirmTarget(null)}
      okText={stopConfirm.okText}
      cancelText="Cancel"
      okButtonProps={confirmTarget === 'symbol' && symbolPaused
        ? { size: 'middle', style: { minWidth: 72, height: 32, background: '#0ECB81', borderColor: '#0ECB81', color: '#0B0E11' } }
        : { danger: true, size: 'middle', style: { minWidth: 72, height: 32 } }}
      cancelButtonProps={{ size: 'middle', style: { minWidth: 72, height: 32, background: '#2B3139', borderColor: '#3d4148', color: '#E0E0E0' } }}
    >
      <Dropdown
        trigger={['click']}
        placement="top"
        disabled={!canStopAll}
        open={menuOpen}
        onOpenChange={setMenuOpen}
        menu={{
          items: [
            symbolPaused
              ? { key: 'symbol', label: `Start ${editingSymbol ?? ''}`.trim(), disabled: !canStop }
              : { key: 'symbol', label: `Stop ${editingSymbol ?? ''}`.trim(), disabled: !canStop },
            { key: 'all', label: stopping ? 'Stop All (re-send)' : 'Stop All', danger: true },
          ],
          onClick: ({ key }) => {
            setMenuOpen(false);
            setConfirmTarget(key as 'symbol' | 'all');
          },
        }}
      >
        <Button
          size="small"
          danger
          icon={<StopOutlined />}
          loading={stoppingGrid || stoppingAll || resumingGrid}
          disabled={!canStopAll}
          title={stopHint}
          style={{
            flex: 1,
            fontWeight: 600,
            fontSize: '12px',
            height: mobile ? '40px' : '32px',
            borderRadius: '4px',
            background: 'transparent',
            borderColor: '#F6465D',
            color: '#F6465D',
          }}
        >
          {stopping ? 'Stopping…' : 'Stop'} {menuOpen ? <DownOutlined /> : <UpOutlined />}
        </Button>
      </Dropdown>
    </Popconfirm>
    <Tooltip title={!canPlace ? (starting ? 'The bot is starting.' : stopping ? 'Wait for the bot to finish stopping.' : 'The grid bot is already running.') : savedForcedClose ? 'Close position is active. Disable it before placing grid.' : formDirty ? 'Please save your changes before placing grid' : undefined}>
      <Button
        size="small"
        icon={<ThunderboltOutlined />}
        onClick={onPlaceGrid}
        loading={placingGrid || starting}
        disabled={formDirty || savedForcedClose || !canPlace}
        style={{
          flex: 1,
          background: (formDirty || savedForcedClose || !canPlace) ? undefined : '#F0B90B',
          borderColor: (formDirty || savedForcedClose || !canPlace) ? undefined : '#F0B90B',
          color: (formDirty || savedForcedClose || !canPlace) ? undefined : '#1E2329',
          fontWeight: 600,
          fontSize: '12px',
          height: mobile ? '40px' : '32px',
          borderRadius: '4px',
        }}
      >
        {starting ? 'Starting…' : 'Place Grid'}
      </Button>
    </Tooltip>
    </>
    )}
  </div>
  );
};

export default ConfigurationSection;
